import { useState } from "react";

interface Props {
  value: string;
  ariaLabel: string;
}

/**
 * Tiny clipboard button — copy glyph that briefly swaps to a check after a
 * successful copy. Ported (simplified) from
 * `_prototype/components/studies/CopyButton.tsx`.
 */
export function CopyButton({ value, ariaLabel }: Props) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — silent fail is fine for an ID copy */
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={ariaLabel}
      title={copied ? "Copied" : "Copy"}
      className="inline-flex items-center justify-center w-5 h-5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
    >
      {copied ? (
        <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
          <path
            d="M3 8.5l3.5 3.5L13 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
          <rect
            x="2"
            y="4"
            width="9"
            height="10"
            rx="1"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M5 4V3a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-1"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}
